import { AlertCircle, Ban, Check, Loader2, PauseCircle } from 'lucide-react';
import { toolLabel, toolNarration } from '../helpers/tool-narration';

export type ToolProgressStatus =
  | 'running'
  | 'ok'
  | 'denied'
  | 'error'
  | 'paused'
  | 'cancelled'
  | 'delivery_unknown'
  | 'partial_delivery';

const STYLES: Record<ToolProgressStatus, string> = {
  delivery_unknown: 'border-amber-200 bg-amber-50 text-amber-700',
  partial_delivery: 'border-amber-200 bg-amber-50 text-amber-700',
  running: 'border-primary-200 bg-primary-30 text-primary-1000',
  ok: 'border-gray-200 bg-gray-50 text-gray-700',
  denied: 'border-amber-200 bg-amber-50 text-amber-700',
  // Staged, waiting on the user — deliberately not the 'ok' styling.
  paused: 'border-secondary-200 bg-secondary-100 text-secondary-1000',
  error: 'border-red-200 bg-red-50 text-red-700',
  cancelled: 'border-gray-200 bg-gray-50 text-gray-500',
};

const ICONS: Record<ToolProgressStatus, React.ReactNode> = {
  delivery_unknown: <AlertCircle className='size-3' />,
  partial_delivery: <AlertCircle className='size-3' />,
  running: <Loader2 className='size-3 animate-spin' />,
  ok: <Check className='size-3' />,
  denied: <Ban className='size-3' />,
  paused: <PauseCircle className='size-3' />,
  error: <AlertCircle className='size-3' />,
  cancelled: <Ban className='size-3' />,
};

/**
 * A live tool-call chip, in plain words in every state (FR-11). It never shows
 * the raw tool id -- not in its text, not in its accessible name -- and never
 * arguments.
 */
export function ToolProgress({
  name,
  status,
}: {
  name: string;
  status: ToolProgressStatus;
}) {
  const known = toolLabel(name);
  const label =
    status === 'delivery_unknown'
      ? 'Delivery unresolved — check receipt'
      : status === 'partial_delivery'
        ? 'Partially delivered — review receipt'
        : status === 'running'
          ? toolNarration(name)
          : status === 'paused'
            ? 'Awaiting your OK'
            : status === 'cancelled'
              ? 'Cancelled'
              : status === 'denied'
                ? known
                  ? `${known} — not allowed`
                  : 'Step not allowed'
                : status === 'error'
                  ? known
                    ? `${known} — failed`
                    : "Couldn't finish a step"
                  : (known ?? 'Done');
  return (
    <span
      role='status'
      data-status={status}
      aria-label={label}
      className={`inline-flex items-center gap-1.5 self-start rounded-full border px-2.5 py-0.5
                  text-[11px] ${STYLES[status]}`}
    >
      {ICONS[status]}
      {label}
    </span>
  );
}
